import React from 'react';
import moment from 'moment';
import {
  getEventName,
  getEventTypeTagClassName,
} from '@op/pages/Overview/KeyMetrics/utils';
import { getLocale } from 'umi';
import { comparableTime } from '@op/utils/time';
import { ParamsTimeType } from '@op/types/common';
import CommonBubble from '@home/components/CommonBubble';
import { getName } from '@home/utils/common';
import { LANGUAGE_EN } from '@home/_config/config';
import { getRealXAxisValue } from '@op/utils/chart';
import { useEventItemPosition } from '../hooks/useEventItemPosition';
import type { IEventItem, AnchorMode } from '../types';

export default function EventItem({
  myChart,
  xAxisValue,
  eventList,
  hide = true,
  format,
  backPositionInfo,
  halfBarWidth,
  maxTimeValue,
  showEventAxis,
  dateType,
  xAxisData,
  yAxisMin,
  hasAnySelected = false,
  anchorMode = 'start',
  endTime,
}: {
  myChart: any;
  xAxisValue: string;
  eventList: Array<IEventItem>;
  hide?: boolean;
  format: string;
  halfBarWidth: number;
  maxTimeValue: string;
  showEventAxis: boolean;
  dateType: ParamsTimeType;
  xAxisData: Array<string>;
  yAxisMin?: boolean;
  hasAnySelected?: boolean;
  anchorMode?: AnchorMode;
  endTime?: string;
  backPositionInfo: (item: {
    eventLine: any;
    barLeftPosition: number;
    barBottomPosition: number;
    eventBox: any;
    anchorMode?: AnchorMode;
    shouldHide?: boolean;
  }) => void;
}) {
  const lang = getLocale();

  const {
    eventBoxRef,
    eventLineRef,
    currentItemPosition,
    renderMarkArea,
    eventLineShow,
    markAreaShow,
    markAreaTimes,
    setRenderMarkArea,
    setEventLineShow,
    setMarkAreaShow,
    setMarkAreaTimes,
    getBarLeftPosition,
  } = useEventItemPosition({
    myChart,
    xAxisValue,
    format,
    dateType,
    xAxisData,
    backPositionInfo,
    yAxisMin,
    anchorMode,
  });

  const handleMouseEnter = (item: IEventItem) => {
    // 没传时间就就不展示标域
    if (!item.start_time && !item.end_time) {
      return;
    }

    const startTime = moment(item.start_time).format(format);
    const useEndTime = item.end_time || endTime;
    const endTimeFormatted = useEndTime ? moment(useEndTime).format(format) : startTime;

    // 根据 anchorMode 决定如何获取 startLeft 和 endLeftValue
    let startLeft: number;
    let endLeftValue: number;

    if (anchorMode === 'end') {
      // end 模式：currentItemPosition.current 是 endTime 的位置
      endLeftValue = currentItemPosition.current + halfBarWidth;

      // 需要计算 startTime 的位置
      const startTimePosition = getBarLeftPosition({ xAxis: startTime });
      startLeft = startTimePosition.left - halfBarWidth;
    } else {
      // start 模式：currentItemPosition.current 是 startTime 的位置
      startLeft = currentItemPosition.current - halfBarWidth;

      // 计算结束时间位置
      if (useEndTime) {
        const maxThanMaxTime = comparableTime(maxTimeValue, endTimeFormatted);
        const realXAxis = maxThanMaxTime ? maxTimeValue : endTimeFormatted;
        const endItem = getBarLeftPosition({ xAxis: realXAxis });
        endLeftValue = endItem.left + halfBarWidth;
      } else {
        endLeftValue = startLeft + halfBarWidth * 2;
      }
    }

    // 标记区域位置始终以 start_time 为起点
    const markLeft = startLeft;
    const width = endLeftValue - startLeft;

    setMarkAreaTimes(anchorMode === 'end' && useEndTime ? endTimeFormatted : startTime);
    setRenderMarkArea({
      left: `${markLeft}px`,
      width: `${width}px`,
      height: eventLineRef.current?.offsetHeight,
      top: eventLineRef.current?.offsetTop + 4,
    });
    setMarkAreaShow(true);
  };

  const handleMouseLeave = () => {
    setMarkAreaShow(false);
    setEventLineShow(false);
    setMarkAreaTimes('');
    setRenderMarkArea({ left: '0', width: '0' });
  };

  // 根据当前事件在 xAxisData 中的位置计算 bubble 的 placement
  const getBubblePlacement = (): 'top' | 'left' | 'right' => {
    // 使用 getRealXAxisValue 转换 xAxisValue 格式，使其与 xAxisData 中的格式一致
    const realXAxisValue = getRealXAxisValue({
      xAxis: xAxisValue,
      dateType,
      format,
      xAxisData,
    });

    const currentIndex = xAxisData?.findIndex(
      (item) => item === realXAxisValue,
    );
    if (currentIndex === -1) return 'top';

    const totalLength = xAxisData?.length;
    const position = currentIndex / totalLength;

    // 前 10% 显示在右侧
    if (position <= 0.1) {
      return 'right';
    }
    // 后 10% 显示在左侧
    if (position >= 0.9) {
      return 'left';
    }
    // 其他情况显示在顶部
    return 'top';
  };

  // 标记区域宽度（用于时间标记位置计算）
  const markAreaWidth = parseFloat(renderMarkArea.width) || 0;

  return (
    <>
      <div
        className={`dbo-mark-area-box ${!showEventAxis ? '--long' : ''} ${
          markAreaShow ? '--show' : '--hide'
        } ${anchorMode === 'end' ? '--anchor-end' : ''}`}
        style={renderMarkArea}
      >
        <div
          className="dbo-mark-area-time"
          style={{
            left: anchorMode === 'start' ? `${halfBarWidth}px` : `${markAreaWidth - halfBarWidth}px`,
          }}
        >
          <p className="dbo-mark-time-item">{markAreaTimes}</p>
        </div>
      </div>
      <div
        ref={eventBoxRef}
        className={`dbo-event-item-wrapper ${hide ? '--hide' : ''} ${hasAnySelected ? '--dimmed' : ''}`}
      >
        <ul
          className="dbo-event-box"
          onMouseLeave={() => handleMouseLeave()}
          onMouseEnter={() => setEventLineShow(true)}
        >
          {eventList.map((item) => (
            <CommonBubble
              key={item}
              content={getName(
                lang === LANGUAGE_EN,
                item?.description,
                item?.description_cn,
              )}
              placement={getBubblePlacement()}
              dark={true}
            >
              <li
                className={`dbo-event-item ${getEventTypeTagClassName(
                  item.event_type,
                )}`}
                key={item.name}
                onMouseEnter={() => handleMouseEnter(item)}
              >
                <span className="dbo-event-item-text">
                  {getEventName(item, lang)}
                </span>
              </li>
            </CommonBubble>
          ))}
        </ul>
      </div>
      <div
        ref={eventLineRef}
        className={`dbo-event-box-line ${eventLineShow ? '--highlight' : ''}`}
      ></div>
    </>
  );
}
